/**
 * @file shaftType.ts
 * @description 磁轴轴体类型表（GCOME_AP 触发行程参数区 字节0 低5bit）。
 *
 * 编码规则（来自竞品网页端逐键设置的 Bus Hound 实测抓包，与我们 GCOME AP 协议一致）：
 *   触发行程参数区 CMD 0xa0/0xa1，每键 8 字节，ESC 键 = 槽位 0：
 *     字节0 = 0xa0 | (轴体类型ID & 0x1f)
 *     高 3bit 固定校验 0x5（0xa0），低 5bit 为轴体类型 ID（0-31）。
 *   实测序列（依次给 ESC 设置不同轴体时的字节0）：
 *     磁玉轴 → 0xa0(0) → 磁玉轴Pro → 0xa1(1) → 磁白轴 → 0xa5(5) →
 *     磁红轴 → 0xa6(6) → 磁茶轴 → 0xa7(7) → 磁金轴 → 0xa8(8)
 *   本表即按该编码结构为出厂 12 款磁轴分配类型 ID（0-11）。
 *
 * 参考网页端（竞品"轴体"页，磁轴/凯华/TTC 分类页签）识图复核：
 *   每个轴体卡片展示 名称 + 可调行程 + 初始压力 + 初始磁通量 + 触底磁通量，
 *   参数格式如"可调行程：0.1-3.4mm / 初始压力：40±5gf / 初始磁通量：135±5gs /
 *   触底磁通量：135±5gs"（截图 OCR 复核，2026-08-15）。
 *
 * 物理参数（可调行程/初始力度/初始通量）为驱动展示与按轴体预设行程用的参考值，
 * 以硬件实测为准（TODO(hardware)：量产前需按规格书复核）。
 */

/** 轴体类型 ID 占位位数（低 5bit，共 32 档）。 */
export const SHAFT_TYPE_ID_BITS = 5
/** 轴体类型 ID 掩码（低 5bit）。 */
export const SHAFT_TYPE_ID_MASK = 0x1f
/** 字节0 固定高位（0x5 << 5 = 0xa0）：固件校验。 */
export const SHAFT_TYPE_HEAD = 0xa0

/** 品牌/系列分组（仿网页端"磁轴/凯华/TTC"分类页签组织轴体列表）。 */
export type ShaftTypeSeries =
  | '磁玉轴'
  | '万磁王轴'
  | '天王磁轴'
  | '凯华'
  | '泰山磁轴'
  | '磁珀'
  | '星岚'
  | '琉璃'

/** 轴体类型 ID 常量（顺序即出厂类型表顺序）。 */
export const ShaftType = {
  CiYuPro: 0, // 磁玉轴 PRO
  CiYuGaming: 1, // 磁玉轴 Gaming
  WanCiWangRgb: 2, // 万磁王轴 RGB
  WanCiWangPom: 3, // 万磁王轴 POM
  WanCiWang: 4, // 万磁王轴
  TianWangEsports: 5, // 天王磁轴-电竞版
  TianWangStandard: 6, // 天王磁轴-标准版
  KailhCiShen: 7, // 凯华磁神轴
  TaiShanGt: 8, // 泰山磁轴 GT
  CiPo: 9, // 磁珀轴
  XingLan: 10, // 星岚磁轴
  LiuLi: 11, // 琉璃磁轴
} as const

/** 轴体类型 ID（0-31，低 5bit）。 */
export type ShaftTypeId = (typeof ShaftType)[keyof typeof ShaftType]

/** 轴体类型功能属性（驱动"选轴体"一键应用预设行程所需的最小集合）。 */
export interface ShaftTypeInfo {
  /** 类型 ID（0-31，低 5bit）。 */
  id: ShaftTypeId
  /** 轴体中文名（UI 展示）。 */
  name: string
  /** 品牌/系列分组。 */
  series: ShaftTypeSeries
  /** 可调行程下限（0.01mm，如 10 = 0.10mm）。 */
  travelMinMm: number
  /** 可调行程上限（0.01mm，如 340 = 3.40mm）。 */
  travelMaxMm: number
  /** 初始力度（gf，公差 ±5gf，UI 展示"初始力"）。 */
  initialForceGf: number
  /** 初始通量（磁轴 AD 采样量程值）。 */
  initialFlux: number
  /** 触发/释放通量（与初始通量成对，按键校准的近端阈值）。 */
  triggerFlux: number
  /** 备注（可选）。 */
  description?: string
}

/**
 * 出厂支持的 12 款磁轴类型表。
 * 顺序与"产品轴体支持清单"一致（磁玉轴 → 琉璃磁轴），ID 连续分配 0-11；
 * 若后续新增轴体，从 12 顺延即可（低 5bit 上限 31）。
 */
export const SHAFT_TYPE_TABLE: readonly ShaftTypeInfo[] = [
  {
    id: ShaftType.CiYuPro,
    name: '磁玉轴PRO',
    series: '磁玉轴',
    travelMinMm: 10,
    travelMaxMm: 340,
    initialForceGf: 45,
    initialFlux: 148,
    triggerFlux: 128,
    description:
      '对标竞品磁玉轴Pro（识图复核：可调行程 0.1-3.4mm、初始压力 40±5gf、初始磁通量 135±5gs、触底磁通量 135±5gs）；本表参数为出厂规格参考值，待硬件复核',
  },
  {
    id: ShaftType.CiYuGaming,
    name: '磁玉轴Gaming',
    series: '磁玉轴',
    travelMinMm: 10,
    travelMaxMm: 340,
    initialForceGf: 30,
    initialFlux: 128,
    triggerFlux: 118,
    description: '磁玉轴的游戏向调校版本（初始力更轻）',
  },
  {
    id: ShaftType.WanCiWangRgb,
    name: '万磁王轴RGB',
    series: '万磁王轴',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '万磁王系列 RGB 透光版',
  },
  {
    id: ShaftType.WanCiWangPom,
    name: '万磁王轴POM',
    series: '万磁王轴',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '万磁王系列 POM 材质版',
  },
  {
    id: ShaftType.WanCiWang,
    name: '万磁王轴',
    series: '万磁王轴',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '万磁王系列标准版',
  },
  {
    id: ShaftType.TianWangEsports,
    name: '天王磁轴-电竞版',
    series: '天王磁轴',
    travelMinMm: 10,
    travelMaxMm: 300,
    initialForceGf: 35,
    initialFlux: 128,
    triggerFlux: 118,
    description: '天王磁轴 电竞特调版',
  },
  {
    id: ShaftType.TianWangStandard,
    name: '天王磁轴-标准版',
    series: '天王磁轴',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 30,
    initialFlux: 125,
    triggerFlux: 115,
    description: '天王磁轴 标准版（初始力 30±5gf）',
  },
  {
    id: ShaftType.KailhCiShen,
    name: '凯华磁神轴',
    series: '凯华',
    travelMinMm: 10,
    travelMaxMm: 300,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '凯华（Kailh）磁神轴',
  },
  {
    id: ShaftType.TaiShanGt,
    name: '泰山磁轴GT',
    series: '泰山磁轴',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 35,
    initialFlux: 125,
    triggerFlux: 115,
    description: '泰山磁轴 GT',
  },
  {
    id: ShaftType.CiPo,
    name: '磁珀轴',
    series: '磁珀',
    travelMinMm: 10,
    travelMaxMm: 300,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '磁珀轴',
  },
  {
    id: ShaftType.XingLan,
    name: '星岚磁轴',
    series: '星岚',
    travelMinMm: 10,
    travelMaxMm: 320,
    initialForceGf: 30,
    initialFlux: 125,
    triggerFlux: 115,
    description: '星岚磁轴',
  },
  {
    id: ShaftType.LiuLi,
    name: '琉璃磁轴',
    series: '琉璃',
    travelMinMm: 10,
    travelMaxMm: 300,
    initialForceGf: 35,
    initialFlux: 135,
    triggerFlux: 125,
    description: '琉璃磁轴',
  },
]

/** 按 ID 索引的只读 Map（初始化时自动校验）。 */
const SHAFT_TYPE_INDEX = new Map<number, ShaftTypeInfo>()
for (const info of SHAFT_TYPE_TABLE) {
  if (!Number.isInteger(info.id) || info.id < 0 || info.id > SHAFT_TYPE_ID_MASK) {
    throw new Error(`轴体类型表 ID 非法（0-${SHAFT_TYPE_ID_MASK}）：${info.id} ${info.name}`)
  }
  if (SHAFT_TYPE_INDEX.has(info.id)) {
    throw new Error(`轴体类型表 ID 重复：${info.id} ${info.name}`)
  }
  SHAFT_TYPE_INDEX.set(info.id, info)
}

/** 按 ID 查轴体类型（未定义返回 undefined）。 */
export function getShaftType(id: number): ShaftTypeInfo | undefined {
  return SHAFT_TYPE_INDEX.get(id)
}

/** 按名称（精确匹配，忽略大小写）查轴体类型。 */
export function findShaftTypeByName(name: string): ShaftTypeInfo | undefined {
  const target = name.trim().toLowerCase()
  return SHAFT_TYPE_TABLE.find((info) => info.name.toLowerCase() === target)
}

/** 校验轴体类型 ID（0-31 整数，非类型表内 ID 也允许——固件可能支持更多预留位）。 */
export function isShaftTypeId(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= SHAFT_TYPE_ID_MASK
}

/**
 * 编码轴体类型 → 触发行程参数区字节0：`0xa0 | (id & 0x1f)`。
 * 高 3bit 固定校验 0x5；与竞品抓包字节级兼容（如 id=5 → 0xa5）。
 * @throws RangeError 非法 ID（非整数或超出 0-31）
 */
export function encodeShaftTypeByte(id: number): number {
  if (!isShaftTypeId(id)) {
    throw new RangeError(`轴体类型 ID 必须为 0-${SHAFT_TYPE_ID_MASK} 的整数，当前为 ${id}`)
  }
  return SHAFT_TYPE_HEAD | (id & SHAFT_TYPE_ID_MASK)
}

/** 从触发行程参数区字节0 解码轴体类型 ID（低 5bit，兼容未知预留位）。 */
export function decodeShaftTypeByte(byte0: number): number {
  return byte0 & SHAFT_TYPE_ID_MASK
}
